/* Vendor Quotation Entry — capture multi-line supplier quotes.
 *
 * The "missing layer" per the architecture: every supplier quote
 * (PDF / email / manual) is captured HERE first, fans out to
 * vendor_prices, and from there Item Master + RFQ read the data.
 *
 * Endpoints:
 *   GET    /api/vendor-quotations
 *   POST   /api/vendor-quotations
 *   GET    /api/vendor-quotations/:id
 *   POST   /api/vendor-quotations/:id/revise
 *   GET    /api/suppliers
 *   GET    /api/item-master
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Plus, Trash2, X, Save, Search, FileText, Sparkles, ChevronRight,
  Truck, Package, Calendar, RefreshCw, AlertTriangle, Wand2, CheckCircle2,
} from 'lucide-react';


const statusTone = {
  draft:      'bg-gray-100 text-gray-700',
  active:     'bg-emerald-100 text-emerald-800',
  superseded: 'bg-amber-100 text-amber-800',
  expired:    'bg-rose-100 text-rose-700',
};


// ── Create / Edit Modal ────────────────────────────────────────────


function QuoteFormModal({ open, onClose, onSaved, suppliers, items, baseQuote, inline = false }) {
  // Default Valid Until = today + 30 days — common standard for supplier quotes.
  const defaultValidUntil = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  })();
  const PT_FIELDS = [
    { key: 'advance_payment',             label: 'Advance Payment' },
    { key: 'design_engineering',          label: 'Design & Engineering' },
    { key: 'after_delivery',              label: 'After Delivery' },
    { key: 'before_delivery',             label: 'Before Delivery' },
    { key: 'after_installation',          label: 'After Installation' },
    { key: 'after_testing_commissioning', label: 'After Testing & Commissioning' },
    { key: 'retention',                   label: 'Retention' },
    { key: 'other_milestone_1',           label: 'Other Milestone 1' },
    { key: 'other_milestone_2',           label: 'Other Milestone 2' },
  ];
  // Default 7-bucket breakdown (all blank — user fills only the relevant ones).
  const blankBreakdown = () => Object.fromEntries(PT_FIELDS.map(f => [f.key, '']));
  const [form, setForm] = useState({
    supplier_id: '', supplier_quote_no: '',
    quote_date: new Date().toISOString().slice(0, 10),
    valid_until: defaultValidUntil, currency: 'SAR', payment_terms: '',
    payment_terms_breakdown: blankBreakdown(),
    general_discount_pct: '', notes: '', default_category: '',
    customer_rfq_id: '',          // optional source link
    customer_rfq_no: '',
  });
  const [lines, setLines] = useState([{ type_of_work: '', item_ref: '', category: '', description: '', size: '', supplier_description: '', quantity: 1, uom: 'ea', unit_price: 0, install_price: 0, lead_time_days: '', offered_brand: '', manufacturer: '', country_of_origin: '' }]);
  const [resolutions, setResolutions] = useState({});   // i → { action, item_code, score }
  const [errors, setErrors]           = useState({});   // i → { missing_fields, suggestions }
  const [busy, setBusy] = useState(false);
  // Customer-RFQ picker — populated lazily after a project is chosen.
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [availableCustomerRfqs, setAvailableCustomerRfqs] = useState([]);

  // Collect existing categories from Item Master (for autocomplete datalist).
  const categories = React.useMemo(() => {
    const set = new Set();
    items.forEach((it) => { if (it.category) set.add(it.category); });
    return Array.from(set).sort();
  }, [items]);

  useEffect(() => {
    if (baseQuote) {
      setForm({
        supplier_id: baseQuote.supplier_id,
        supplier_quote_no: baseQuote.supplier_quote_no || '',
        quote_date: new Date().toISOString().slice(0, 10),
        valid_until: baseQuote.valid_until || defaultValidUntil,
        currency: baseQuote.currency || 'SAR',
        payment_terms: baseQuote.payment_terms || '',
        payment_terms_breakdown: { ...blankBreakdown(), ...(baseQuote.payment_terms_breakdown || {}) },
        general_discount_pct: baseQuote.general_discount_pct || '',
        notes: '',
        default_category: baseQuote.default_category || '',
      });
      setLines((baseQuote.lines || []).map((l) => ({
        type_of_work: l.type_of_work || '',
        category: l.category || '', description: l.description || '',
        size: l.size || '',
        supplier_description: l.supplier_description || '',
        quantity: l.quantity || 1, uom: l.uom || 'ea',
        unit_price: l.unit_price || 0,
        install_price: l.install_price || 0,
        lead_time_days: l.lead_time_days || '',
        item_code: l.item_code,
      })));
    } else if (open) {
      const todayPlus30 = new Date();
      todayPlus30.setDate(todayPlus30.getDate() + 30);
      setForm({
        supplier_id: '', supplier_quote_no: '',
        quote_date: new Date().toISOString().slice(0, 10),
        valid_until: todayPlus30.toISOString().slice(0, 10),
        currency: 'SAR', payment_terms: '',
        payment_terms_breakdown: blankBreakdown(),
        general_discount_pct: '', notes: '', default_category: '',
      });
      setLines([{ type_of_work: '', item_ref: '', category: '', description: '', size: '', supplier_description: '', quantity: 1, uom: 'ea', unit_price: 0, install_price: 0, lead_time_days: '', offered_brand: '', manufacturer: '', country_of_origin: '' }]);
      setResolutions({});
      setErrors({});
    }
  }, [open, baseQuote]);

  // Lookups for the Customer-RFQ picker (only on New, not Revise).
  useEffect(() => {
    if (!open || baseQuote) return;
    axiosInstance.get('/projects?limit=500')
      .then(r => setProjects(Array.isArray(r.data) ? r.data : (r.data?.projects || [])))
      .catch(() => setProjects([]));
  }, [open, baseQuote]);

  useEffect(() => {
    if (!open || baseQuote) return;
    if (!projectId) { setAvailableCustomerRfqs([]); return; }
    axiosInstance.get(`/price-comparisons/meta/customer-rfqs?project_id=${projectId}`)
      .then(r => setAvailableCustomerRfqs(r.data?.customer_rfqs || []))
      .catch(() => setAvailableCustomerRfqs([]));
  }, [open, baseQuote, projectId]);

  const importFromCustomerRfq = (rfqId) => {
    const rfq = availableCustomerRfqs.find(r => r.id === rfqId);
    if (!rfq) {
      setForm(f => ({ ...f, customer_rfq_id: '', customer_rfq_no: '' }));
      return;
    }
    setForm(f => ({ ...f, customer_rfq_id: rfq.id, customer_rfq_no: rfq.rfq_no }));
    // Pre-fill the lines table with the RFQ items — prices left at 0
    // for the user to fill in with the supplier's prices.
    const newLines = (rfq.lines || []).map(ln => ({
      type_of_work: ln.type_of_work || '',
      item_ref:     ln.item_ref || '',
      category:     ln.category || '',
      description:  ln.description || '',
      size:         ln.size || '',
      supplier_description: '',
      quantity:     ln.qty || 1,
      uom:          ln.unit || 'ea',
      unit_price:   0,
      install_price: 0,
      lead_time_days: '',
      // Brand block carried from RFQ — supplier may amend `offered_brand`.
      requested_brand:    ln.requested_brand || '',
      brand_strictness:   ln.brand_strictness || '',
      offered_brand:      ln.requested_brand || '',  // default = same as requested
      manufacturer:       '',
      country_of_origin:  '',
      // surface RFQ provenance per line for the audit trail
      source_customer_rfq_id: rfq.id,
      source_customer_rfq_no: rfq.rfq_no,
      source_line_no: ln.no,
    }));
    setLines(newLines.length > 0 ? newLines :
      [{ type_of_work: '', item_ref: '', category: '', description: '', size: '', supplier_description: '', quantity: 1, uom: 'ea', unit_price: 0, install_price: 0, lead_time_days: '', offered_brand: '', manufacturer: '', country_of_origin: '' }]);
    toast.success(`Imported ${newLines.length} line(s) from ${rfq.rfq_no}`);
  };

  if (!open) return null;

  // For "Supply & Installation" rows, add install_price * qty to the line total.
  const lineTotal = (l) => {
    const qty   = Number(l.quantity)   || 0;
    const up    = Number(l.unit_price) || 0;
    const ip    = Number(l.install_price) || 0;
    const isSI  = l.type_of_work === 'Supply & Installation';
    return qty * (up + (isSI ? ip : 0));
  };
  const hasInstallColumn = lines.some((l) => l.type_of_work === 'Supply & Installation');
  const subtotal = lines.reduce((s, l) => s + lineTotal(l), 0);
  const discAmt  = subtotal * (Number(form.general_discount_pct) || 0) / 100;
  const grand    = subtotal - discAmt;

  const updateLine = (i, key, val) => {
    const newLines = [...lines];
    newLines[i] = { ...newLines[i], [key]: val };
    setLines(newLines);
    // Auto-preview when description blurs / category changes.
    if (key === 'description' || key === 'category') {
      // Debounce a bit
      previewLine(i, newLines[i]);
    }
  };

  const previewLine = async (i, line) => {
    const cat = line.category || form.default_category;
    if (!line.description || line.description.length < 3) {
      const next = { ...resolutions }; delete next[i]; setResolutions(next);
      return;
    }
    try {
      const r = await axiosInstance.post('/vendor-quotations/preview-resolution', {
        category: cat, description: line.description,
      });
      setResolutions((prev) => ({ ...prev, [i]: r.data }));
    } catch (_) { /* silent */ }
  };

  const applyAutoFix = (i, field, value) => {
    const newLines = [...lines];
    newLines[i] = { ...newLines[i], [field]: value };
    setLines(newLines);
    // Clear that specific field's error.
    setErrors((prev) => {
      const cur = prev[i];
      if (!cur) return prev;
      const remaining = cur.missing_fields.filter((f) => f !== field);
      if (remaining.length === 0) {
        const next = { ...prev }; delete next[i]; return next;
      }
      return { ...prev, [i]: { ...cur, missing_fields: remaining } };
    });
    toast.success(`Auto-filled ${field}`);
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axiosInstance.post(
        '/vendor-quotations/upload-attachment', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setForm((prev) => ({ ...prev,
                            attachment_url:      r.data.attachment_url,
                            attachment_filename: r.data.filename || file.name }));
      toast.success(`📎 Uploaded: ${file.name}`);
    } catch (_) {
      toast.error('Upload failed');
    }
  };

  const submit = async () => {
    if (!form.supplier_id) { toast.error('Choose a supplier'); return; }
    // Guard: if any Payment Terms bucket is filled the total must equal 100%.
    const ptTotal = PT_FIELDS.reduce(
      (s, f) => s + (Number((form.payment_terms_breakdown || {})[f.key]) || 0), 0,
    );
    if (ptTotal !== 0 && ptTotal !== 100) {
      toast.error(`Payment Terms must total 100% (currently ${ptTotal}%)`);
      return;
    }
    setBusy(true);
    setErrors({});
    try {
      const payload = {
        ...form,
        default_category: form.default_category || undefined,
        attachment_url: form.attachment_url || undefined,
        general_discount_pct: form.general_discount_pct ? Number(form.general_discount_pct) : undefined,
        // Optional Customer RFQ link — surfaces provenance on the quote.
        customer_rfq_id: form.customer_rfq_id || undefined,
        customer_rfq_no: form.customer_rfq_no || undefined,
        // Send ALL lines as-is so validation indices match the UI.
        lines: lines.map((l) => ({
          type_of_work: l.type_of_work || undefined,
          item_ref:     l.item_ref || undefined,
          category:    l.category || undefined,
          description: l.description,
          size:        l.size || undefined,
          supplier_description: l.supplier_description || undefined,
          quantity:    l.quantity === '' ? 0 : Number(l.quantity),
          uom:         l.uom || '',
          unit_price:  l.unit_price === '' ? 0 : Number(l.unit_price),
          install_price: (l.type_of_work === 'Supply & Installation' && l.install_price !== '')
            ? Number(l.install_price) : undefined,
          lead_time_days: l.lead_time_days ? Number(l.lead_time_days) : undefined,
          requested_brand:   l.requested_brand || undefined,
          brand_strictness:  l.brand_strictness || undefined,
          offered_brand:     l.offered_brand || undefined,
          manufacturer:      l.manufacturer || undefined,
          country_of_origin: l.country_of_origin || undefined,
          notes:       l.notes,
        })),
      };
      let r;
      if (baseQuote) {
        r = await axiosInstance.post(`/vendor-quotations/${baseQuote.id}/revise`, payload);
        toast.success(`Revised → ${r.data.quote_no} (V${r.data.revision_number})`);
      } else {
        r = await axiosInstance.post('/vendor-quotations', payload);
        const created = (r.data.resolution_audit || []).filter((a) => a.action === 'create').length;
        const reused  = (r.data.resolution_audit || []).filter((a) => a.action === 'reuse').length;
        toast.success(`${r.data.quote_no} created · ${r.data.price_rows_written} price(s) updated · ${created} new item(s) · ${reused} matched existing`);
      }
      onSaved(r.data);
      onClose();
    } catch (e) {
      const status = e?.response?.status;
      const d = e?.response?.data?.detail;
      if (status === 422 && d?.error === 'VALIDATION_FAILED' && Array.isArray(d.invalid_lines)) {
        const errMap = {};
        d.invalid_lines.forEach((iv) => { errMap[iv.index] = iv; });
        setErrors(errMap);
        const firstIdx = d.invalid_lines[0]?.index;
        toast.error(`${d.invalid_lines.length} line(s) need attention — please complete missing fields`);
        // Scroll + focus the first invalid line.
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="vq-line-${firstIdx}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const firstMissing = d.invalid_lines[0].missing_fields[0];
            const inp = document.querySelector(
              `[data-testid="vq-line-${firstMissing === 'description' ? 'desc' : firstMissing === 'unit_price' ? 'price' : firstMissing}-${firstIdx}"]`
            );
            inp?.focus?.();
          }
        }, 100);
      } else {
        toast.error((d && (d.error || d.message)) || 'Save failed');
      }
    } finally { setBusy(false); }
  };

  return (
    <div className={inline
        ? 'bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col'
        : 'fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4'}
         data-testid="vq-form-modal">
      <div className={inline
          ? 'w-full flex flex-col'
          : 'bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col'}>
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="font-semibold inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            {baseQuote ? `Revise ${baseQuote.quote_no}` : 'New Vendor Quotation'}
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400 hover:text-gray-700" /></button>
        </div>

        <div className={`p-4 space-y-4 ${inline ? '' : 'overflow-auto'}`}>
          {/* Customer RFQ picker — only when creating new (not revising). */}
          {!baseQuote && (
            <div className="border border-sky-200 bg-sky-50/40 rounded-lg p-3"
                 data-testid="vq-form-rfq-picker">
              <p className="text-xs font-semibold text-sky-900 mb-2 inline-flex items-center gap-1">
                🔗 Import from Customer RFQ <span className="text-gray-500 font-normal">(optional)</span>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <select value={projectId}
                         onChange={(e) => setProjectId(e.target.value)}
                         data-testid="vq-form-project-filter"
                         className="text-sm border border-sky-300 bg-white rounded px-2 py-1.5">
                  <option value="">— select a Project to surface its RFQs —</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.project_code ? `${p.project_code} · ` : ''}{p.name}
                    </option>
                  ))}
                </select>
                <select value={form.customer_rfq_id}
                         onChange={(e) => importFromCustomerRfq(e.target.value)}
                         disabled={!projectId}
                         data-testid="vq-form-rfq-select"
                         className="text-sm border border-sky-300 bg-white rounded px-2 py-1.5 disabled:opacity-50">
                  <option value="">
                    {projectId
                      ? (availableCustomerRfqs.length ? '— pick an RFQ Number —' : '— no RFQs for this project —')
                      : '— pick a project first —'}
                  </option>
                  {availableCustomerRfqs.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.rfq_no} · {r.rfq_date} · {(r.lines || []).length} line(s)
                    </option>
                  ))}
                </select>
              </div>
              {form.customer_rfq_no && (
                <p className="mt-1 text-[11px] text-emerald-700 font-semibold">
                  ✓ Imported from {form.customer_rfq_no} — fill in your supplier's unit prices below.
                </p>
              )}
            </div>
          )}

          {/* Header */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-700 font-semibold">Supplier *</label>
              <select value={form.supplier_id} disabled={!!baseQuote}
                       onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                       data-testid="vq-form-supplier"
                       className="w-full text-sm border border-gray-300 rounded px-2 py-1.5">
                <option value="">— select —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name_english} — {s.name_arabic}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-700 font-semibold">Supplier Quote No.</label>
              <input value={form.supplier_quote_no}
                      onChange={(e) => setForm({ ...form, supplier_quote_no: e.target.value })}
                      placeholder="Supplier's reference"
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="text-xs text-gray-700 font-semibold">Quote Date</label>
              <input type="date" value={form.quote_date}
                      onChange={(e) => setForm({ ...form, quote_date: e.target.value })}
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="text-xs text-gray-700 font-semibold">Valid Until</label>
              <input type="date" value={form.valid_until}
                      onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="text-xs text-gray-700 font-semibold">Currency</label>
              <input value={form.currency}
                      onChange={(e) => setForm({ ...form, currency: e.target.value })}
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
            </div>
            <div className="col-span-2 border border-violet-200 bg-violet-50/30 rounded-lg p-3">
              {(() => {
                const bd     = form.payment_terms_breakdown || blankBreakdown();
                const total  = PT_FIELDS.reduce((s, f) => s + (Number(bd[f.key]) || 0), 0);
                const isValid = total === 100;
                const updateBd = (key, val) => {
                  // Restrict to 0-100 and propagate updates including summary text.
                  let v = val === '' ? '' : Math.max(0, Math.min(100, Number(val)));
                  const next = { ...bd, [key]: v === '' ? '' : v };
                  const summary = PT_FIELDS
                    .filter(f => Number(next[f.key]) > 0)
                    .map(f => `${f.label} ${Number(next[f.key])}%`)
                    .join(' · ');
                  setForm({ ...form, payment_terms_breakdown: next, payment_terms: summary });
                };
                const toneCls = total === 0
                  ? 'bg-gray-200 text-gray-600'
                  : isValid
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                    : 'bg-rose-100 text-rose-800 border border-rose-300';
                return (
                  <>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <label className="text-xs text-violet-900 font-bold inline-flex items-center gap-1">
                        💳 Payment Terms — Breakdown (%)
                      </label>
                      <span data-testid="vq-payment-terms-total"
                            className={`text-[10px] px-2 py-0.5 rounded font-semibold font-mono ${toneCls}`}>
                        Total: {total}%
                        {total > 0 && !isValid && (total < 100 ? ` · ${100 - total}% remaining` : ` · over by ${total - 100}%`)}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        Fill only the buckets you use — the total must equal exactly 100%.
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1.5">
                      {PT_FIELDS.map((f) => (
                        <div key={f.key} className="flex items-center gap-2">
                          <label htmlFor={`pt-${f.key}`}
                                  className="text-xs text-gray-700 flex-1">{f.label}</label>
                          <div className="relative">
                            <input id={`pt-${f.key}`} type="number" step="0.01" min="0" max="100"
                                    value={bd[f.key]}
                                    onChange={(e) => updateBd(f.key, e.target.value)}
                                    data-testid={`vq-pt-${f.key}`}
                                    className="w-20 text-xs text-right border border-gray-300 rounded px-1.5 py-1 pr-5 font-mono" />
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
            <div>
              <label className="text-xs text-gray-700 font-semibold">General Discount %</label>
              <input type="number" step="0.01" min="0" max="100"
                      value={form.general_discount_pct}
                      onChange={(e) => setForm({ ...form, general_discount_pct: e.target.value })}
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="text-xs text-gray-700 font-semibold">Default Category</label>
              <input list="vq-categories-list" value={form.default_category}
                      onChange={(e) => setForm({ ...form, default_category: e.target.value })}
                      placeholder="e.g. Cables, Pipes, Valves"
                      data-testid="vq-form-default-category"
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
              <datalist id="vq-categories-list">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div className="col-span-2 border-2 border-dashed border-indigo-300 bg-indigo-50/40 rounded-lg p-3">
              <label className="text-sm text-indigo-900 font-bold inline-flex items-center gap-1 mb-2">
                📎 تحميل عرض السعر · Upload Vendor Quotation File (PDF/image)
              </label>
              <div className="flex items-center gap-2">
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                        onChange={handleAttachmentUpload}
                        data-testid="vq-form-attachment-input"
                        className="text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0
                                    file:text-xs file:font-semibold file:bg-indigo-600 file:text-white
                                    hover:file:bg-indigo-700 flex-1 cursor-pointer" />
                {form.attachment_url && (
                  <a href={form.attachment_url} target="_blank" rel="noopener noreferrer"
                      data-testid="vq-form-attachment-link"
                      className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-200">
                    ✓ {form.attachment_filename || 'View'}
                  </a>
                )}
              </div>
              <div className="text-[10px] text-indigo-700 mt-1">
                ارفع نسخة من عرض المورد الأصلي (PDF أو صورة) — حد أقصى 10MB
              </div>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-700 font-semibold">Notes</label>
              <input value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
            </div>
          </div>

          {/* Lines */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 font-semibold text-sm flex items-center justify-between">
              <span className="inline-flex items-center gap-2"><Package className="w-4 h-4" /> Quote Lines</span>
              <span className="text-[10px] text-gray-500">
                💡 Enter <strong>Category + Description</strong> — the system auto-generates the Item Code (CAT-XXX-NNNNNN)
              </span>
              <button onClick={() => setLines([...lines, { type_of_work: '', item_ref: '', category: '', description: '', size: '', supplier_description: '', quantity: 1, uom: 'ea', unit_price: 0, install_price: 0, lead_time_days: '', offered_brand: '', manufacturer: '', country_of_origin: '' }])}
                      data-testid="vq-add-line"
                      className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add line
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-600 bg-gray-50">
                <tr>
                  <th className="text-left px-2 py-1 w-28">Type of work</th>
                  <th className="text-left px-2 py-1 w-24">Item Ref</th>
                  <th className="text-left px-2 py-1 w-32">Category</th>
                  <th className="text-left px-2 py-1">Description *</th>
                  <th className="text-left px-2 py-1 w-24">Size</th>
                  <th className="text-left px-2 py-1">Supplier Description</th>
                  <th className="text-left px-2 py-1 w-40">Brand · Mfg · Origin</th>
                  <th className="text-right px-2 py-1 w-16">Qty</th>
                  <th className="text-left px-2 py-1 w-20">Unit</th>
                  <th className="text-right px-2 py-1 w-24">Unit Price *</th>
                  <th className="text-right px-2 py-1 w-24">Time Delivery</th>
                  {hasInstallColumn && (
                    <th className="text-right px-2 py-1 w-24">Install Price</th>
                  )}
                  <th className="text-right px-2 py-1 w-24">Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const res = resolutions[i];
                  const err = errors[i];
                  const missing = new Set(err?.missing_fields || []);
                  const sug     = err?.suggestions || {};
                  const fieldCls = (f) => missing.has(f)
                    ? 'border-rose-400 bg-rose-50 focus:border-rose-500 focus:ring-rose-200'
                    : 'border-gray-300';
                  const rowCls = err
                    ? 'border-t border-rose-300 bg-rose-50/30'
                    : 'border-t border-gray-100';
                  return (
                  <React.Fragment key={i}>
                  <tr className={rowCls} data-testid={`vq-line-${i}`}>
                    <td className="px-2 py-1">
                      <select value={l.type_of_work || ''}
                              onChange={(e) => updateLine(i, 'type_of_work', e.target.value)}
                              data-testid={`vq-line-type-of-work-${i}`}
                              className="w-full text-xs border border-gray-300 rounded px-1 py-1 bg-white">
                        <option value="">— optional —</option>
                        <option value="Supply">Supply</option>
                        <option value="Installation">Installation</option>
                        <option value="Supply & Installation">Supply &amp; Installation</option>
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      <input value={l.item_ref || ''}
                              readOnly={!!l.source_customer_rfq_id}
                              tabIndex={l.source_customer_rfq_id ? -1 : 0}
                              onChange={(e) => updateLine(i, 'item_ref', e.target.value)}
                              placeholder="—"
                              data-testid={`vq-line-itemref-${i}`}
                              className={`w-full text-xs border border-gray-300 rounded px-1 py-1 font-mono ${l.source_customer_rfq_id ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : ''}`} />
                    </td>
                    <td className="px-2 py-1">
                      <input list="vq-categories-list"
                              value={l.category}
                              readOnly
                              tabIndex={-1}
                              placeholder={form.default_category || 'category'}
                              data-testid={`vq-line-category-${i}`}
                              className={`w-full text-xs border rounded px-1 py-1 bg-gray-100 text-gray-700 cursor-not-allowed ${fieldCls('category')}`} />
                    </td>
                    <td className="px-2 py-1">
                      <textarea value={l.description}
                              readOnly
                              tabIndex={-1}
                              placeholder="e.g. PVC pipe 110mm 6m schedule 40&#10;(multi-line ok)"
                              rows={2}
                              data-testid={`vq-line-desc-${i}`}
                              className={`w-full text-xs border rounded px-1 py-1 resize-y bg-gray-100 text-gray-700 cursor-not-allowed ${fieldCls('description')}`} />
                    </td>
                    <td className="px-2 py-1">
                      <textarea value={l.size || ''}
                              readOnly
                              tabIndex={-1}
                              placeholder="size"
                              rows={2}
                              data-testid={`vq-line-size-${i}`}
                              className="w-full text-xs border border-gray-300 rounded px-1 py-1 resize-y bg-gray-100 text-gray-700 cursor-not-allowed" />
                    </td>
                    <td className="px-2 py-1">
                      <textarea value={l.supplier_description || ''}
                              onChange={(e) => updateLine(i, 'supplier_description', e.target.value)}
                              placeholder="Supplier's own description (optional)"
                              rows={2}
                              data-testid={`vq-line-supplier-desc-${i}`}
                              className="w-full text-xs border border-gray-300 rounded px-1 py-1 resize-y" />
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex flex-col gap-1">
                        {l.requested_brand && (
                          <div className="text-[9px] text-gray-500 leading-tight"
                                title={`Customer requested: ${l.requested_brand} (${l.brand_strictness || 'Open'})`}>
                            req: <span className="font-mono text-indigo-700">{l.requested_brand}</span>
                            {l.brand_strictness === 'Specified' && (
                              <span className="ml-1 px-1 rounded bg-rose-100 text-rose-700 font-semibold">strict</span>
                            )}
                          </div>
                        )}
                        <input value={l.offered_brand || ''}
                                onChange={(e) => updateLine(i, 'offered_brand', e.target.value)}
                                placeholder="Offered brand"
                                data-testid={`vq-line-brand-${i}`}
                                className="w-full text-[11px] border border-gray-300 rounded px-1 py-0.5" />
                        <input value={l.manufacturer || ''}
                                onChange={(e) => updateLine(i, 'manufacturer', e.target.value)}
                                placeholder="Manufacturer"
                                data-testid={`vq-line-mfg-${i}`}
                                className="w-full text-[11px] border border-gray-300 rounded px-1 py-0.5" />
                        <input value={l.country_of_origin || ''}
                                onChange={(e) => updateLine(i, 'country_of_origin', e.target.value)}
                                placeholder="Country of Origin"
                                data-testid={`vq-line-origin-${i}`}
                                className="w-full text-[11px] border border-gray-300 rounded px-1 py-0.5" />
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" step="0.01" value={l.quantity}
                              onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                              data-testid={`vq-line-quantity-${i}`}
                              className={`w-full text-xs text-right border rounded px-1 py-1 ${fieldCls('quantity')}`} />
                    </td>
                    <td className="px-2 py-1">
                      <input value={l.uom}
                              onChange={(e) => updateLine(i, 'uom', e.target.value)}
                              placeholder="ea"
                              data-testid={`vq-line-uom-${i}`}
                              className={`w-full text-xs border rounded px-1 py-1 ${fieldCls('uom')}`} />
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" step="0.01" value={l.unit_price}
                              onChange={(e) => updateLine(i, 'unit_price', e.target.value)}
                              data-testid={`vq-line-price-${i}`}
                              className={`w-full text-xs text-right border rounded px-1 py-1 ${fieldCls('unit_price')}`} />
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" min="0" value={l.lead_time_days}
                              onChange={(e) => updateLine(i, 'lead_time_days', e.target.value)}
                              placeholder="days"
                              className="w-full text-xs text-right border border-gray-300 rounded px-1 py-1" />
                    </td>
                    {hasInstallColumn && (
                      <td className="px-2 py-1">
                        {l.type_of_work === 'Supply & Installation' ? (
                          <input type="number" step="0.01" value={l.install_price ?? 0}
                                  onChange={(e) => updateLine(i, 'install_price', e.target.value)}
                                  data-testid={`vq-line-install-price-${i}`}
                                  className="w-full text-xs text-right border border-gray-300 rounded px-1 py-1" />
                        ) : (
                          <input type="number" value="" disabled placeholder="—"
                                  className="w-full text-xs text-right border border-gray-200 bg-gray-100 rounded px-1 py-1 cursor-not-allowed" />
                        )}
                      </td>
                    )}
                    <td className="px-2 py-1 text-right font-mono text-xs"
                        data-testid={`vq-line-total-${i}`}>
                      {lineTotal(l).toLocaleString()}
                    </td>
                    <td className="px-2 py-1">
                      <button onClick={() => {
                                setLines(lines.filter((_, j) => j !== i));
                                setErrors((prev) => { const n = { ...prev }; delete n[i]; return n; });
                              }}
                              disabled={lines.length === 1}
                              className="text-gray-400 hover:text-rose-600 disabled:opacity-30">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                  {/* Resolution preview (smart catalog) */}
                  {res && l.description && l.description.length >= 3 && !err && (
                    <tr className="border-t border-dashed border-indigo-200 bg-indigo-50/40">
                      <td colSpan={hasInstallColumn ? 14 : 13} className="px-3 py-1 text-[11px]"
                          data-testid={`vq-resolution-preview-${i}`}>
                        {res.action === 'reuse' ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700">
                            ♻️ Will reuse <strong className="font-mono">{res.item_code}</strong>
                            <span className="text-gray-500">
                              (matched "{res.matched_description?.slice(0, 60)}" · score {Math.round(res.match_score * 100)}%)
                            </span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-indigo-700">
                            ✨ Will auto-create <strong className="font-mono">{res.item_code}</strong>
                            <span className="text-gray-500">(category: {res.category})</span>
                          </span>
                        )}
                      </td>
                    </tr>
                  )}
                  {/* Smart Error Assistant — appears when this line has validation errors */}
                  {err && (
                    <tr className="border-t border-dashed border-rose-300 bg-rose-50/70">
                      <td colSpan={hasInstallColumn ? 14 : 13} className="px-3 py-2"
                          data-testid={`vq-line-errors-${i}`}>
                        <div className="flex items-start gap-2 text-xs">
                          <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 space-y-1">
                            <div className="font-semibold text-rose-800">
                              Missing required: {err.missing_fields.map((f) => f.replace('_', ' ')).join(', ')}
                            </div>
                            {/* Suggestions */}
                            {Object.entries(sug).map(([field, info]) => {
                              const val = info.unit_price ?? info.value;
                              const src = info.from || info.source;
                              return (
                                <div key={field} className="flex items-center gap-2"
                                     data-testid={`vq-line-suggestion-${i}-${field}`}>
                                  <span className="text-gray-700">
                                    💡 Suggested {field.replace('_', ' ')}: <strong>{val}</strong>
                                    <span className="text-gray-500 ml-1">({src})</span>
                                  </span>
                                  <button
                                    onClick={() => applyAutoFix(i, field, val)}
                                    data-testid={`vq-line-autofix-${i}-${field}`}
                                    className="px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold inline-flex items-center gap-1">
                                    <Wand2 className="w-3 h-3" /> Apply
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="border-t border-gray-200 bg-gray-50 text-sm">
                <tr><td colSpan={hasInstallColumn ? 12 : 11} className="px-2 py-1 text-right text-gray-600">Subtotal</td>
                    <td className="px-2 py-1 text-right font-mono font-semibold">{subtotal.toLocaleString()}</td><td></td></tr>
                <tr><td colSpan={hasInstallColumn ? 12 : 11} className="px-2 py-1 text-right text-gray-600">Discount</td>
                    <td className="px-2 py-1 text-right font-mono text-rose-700">−{discAmt.toLocaleString()}</td><td></td></tr>
                <tr><td colSpan={hasInstallColumn ? 12 : 11} className="px-2 py-1 text-right font-semibold">Grand Total</td>
                    <td className="px-2 py-1 text-right font-mono font-bold text-indigo-700">{grand.toLocaleString()} {form.currency}</td><td></td></tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2 bg-gray-50">
          {Object.keys(errors).length > 0 && (
            <span className="mr-auto text-xs text-rose-700 inline-flex items-center gap-1"
                  data-testid="vq-error-summary">
              <AlertTriangle className="w-4 h-4" />
              {Object.keys(errors).length} line(s) need attention
            </span>
          )}
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-100">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="vq-form-submit"
                  className="px-4 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
            <Save className="w-4 h-4" /> {baseQuote ? 'Save Revision' : 'Save Quotation'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── List Page ──────────────────────────────────────────────────────


export default function VendorQuotations() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  // Unpriced Customer RFQs surfaced at the top of the listing as red
  // rows so the pricing team notices them immediately (and can click
  // through to a new VQ pre-linked to that RFQ).
  const [unpricedRfqs, setUnpricedRfqs] = useState([]);
  // The currently-viewed unpriced RFQ (read-only modal). null = closed.
  // Replaces the previous behaviour of navigating away to /new on click —
  // the user wanted a quick peek at the RFQ contents first, then a
  // deliberate button to start pricing it.
  const [viewingRfq, setViewingRfq] = useState(null);
  const [filter, setFilter] = useState({ supplier_id: '', status: '' });
  const [latestOnly, setLatestOnly] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [baseQuote, setBaseQuote] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const params = new URLSearchParams({ latest_only: latestOnly, limit: 200 });
      if (filter.supplier_id) params.set('supplier_id', filter.supplier_id);
      if (filter.status) params.set('status', filter.status);
      // Fan-out: in parallel with the VQ list we also pull every Customer
      // RFQ whose ``pricing_status === 'unpriced'`` so the pricing team
      // sees them as red rows above the actual quotations.
      const [list, sup, ims, unpriced] = await Promise.all([
        axiosInstance.get(`/vendor-quotations?${params.toString()}`),
        axiosInstance.get('/suppliers'),
        axiosInstance.get('/item-master?limit=500'),
        axiosInstance.get('/customer-rfqs/unpriced').catch(() => ({ data: [] })),
      ]);
      setRows(list.data?.quotations || []);
      setTotal(list.data?.total || 0);
      setSuppliers(sup.data || []);
      setItems(ims.data?.items || []);
      setUnpricedRfqs(Array.isArray(unpriced.data) ? unpriced.data : []);
    } catch (_) { toast.error('Failed to load quotations'); }
  }, [filter.supplier_id, filter.status, latestOnly]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="p-6 space-y-4" data-testid="vendor-quotations-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-indigo-600" /> Vendor Quotations
            <span className="text-sm font-mono px-2 py-0.5 rounded bg-gray-900 text-white"
                  data-testid="vq-total-count">{total}</span>
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            The capture layer for every supplier quote. Each line flows automatically
            into Item Master & RFQ price suggestions.
          </p>
        </div>
        <button onClick={() => navigate('/vendor-quotations/new')}
                data-testid="vq-new-button"
                className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> New Quotation
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <select value={filter.supplier_id}
                 onChange={(e) => setFilter({ ...filter, supplier_id: e.target.value })}
                 data-testid="vq-filter-supplier"
                 className="text-sm border border-gray-300 rounded px-2 py-1.5">
          <option value="">All suppliers</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>{s.name_english}</option>
          ))}
        </select>
        <select value={filter.status}
                 onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                 className="text-sm border border-gray-300 rounded px-2 py-1.5">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="superseded">Superseded</option>
          <option value="expired">Expired</option>
          <option value="draft">Draft</option>
        </select>
        <label className="text-xs inline-flex items-center gap-1 cursor-pointer">
          <input type="checkbox" checked={latestOnly}
                  onChange={(e) => setLatestOnly(e.target.checked)}
                  data-testid="vq-latest-toggle" />
          Latest revision only
        </label>
        <button onClick={refresh} className="ml-auto text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">Quote No.</th>
              <th className="text-left px-3 py-2">Supplier</th>
              <th className="text-left px-3 py-2">Quote Date</th>
              <th className="text-left px-3 py-2">Valid Until</th>
              <th className="text-left px-3 py-2">Payment Terms</th>
              <th className="text-right px-3 py-2">Lines</th>
              <th className="text-right px-3 py-2">Grand Total</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">V</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {/* Unpriced Customer RFQs — surfaced in red so the pricing
                team notices and can click to create a Vendor Quotation
                linked to the RFQ. Each row carries the RFQ no, date,
                customer, project, contact and line-count so it reads
                like the row spec in the issue ticket. */}
            {unpricedRfqs.map((r) => (
              <tr key={`rfq-${r.id}`}
                  className="border-t border-gray-100 bg-red-50 hover:bg-red-100 cursor-pointer border-l-4 border-l-red-500"
                  onClick={() => setViewingRfq(r)}
                  data-testid={`vq-unpriced-rfq-${r.rfq_no}`}>
                <td className="px-3 py-2 font-mono text-xs text-red-700 font-bold">{r.rfq_no}</td>
                <td className="px-3 py-2 text-sm inline-flex items-center gap-1">
                  <FileText className="w-3 h-3 text-red-400" />
                  <span className="text-red-700">
                    {r.customer_name || '—'}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-700">{r.project_name || '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-700">{r.contact_person || '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{r.rfq_date || ''}</td>
                <td className="px-3 py-2 text-xs text-gray-600">{(r.lines || []).length} lines</td>
                <td colSpan={4} className="px-3 py-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold uppercase tracking-wider text-[10px]">
                    Not Processed · لم يُعالَج
                  </span>
                  <button type="button"
                          onClick={(e) => { e.stopPropagation(); navigate(`/vendor-quotations/new?linked_rfq=${r.id}`); }}
                          data-testid={`vq-unpriced-rfq-${r.rfq_no}-create-link`}
                          className="ml-2 text-red-700 font-semibold underline hover:text-red-900">
                    Create linked VQ →
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && unpricedRfqs.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-gray-500">
                No vendor quotations yet. Click <strong>New Quotation</strong> to start.
              </td></tr>
            )}
            {rows.map((q) => (
              <tr key={q.id}
                  className="border-t border-gray-100 hover:bg-indigo-50 cursor-pointer"
                  onClick={() => navigate(`/vendor-quotations/${q.id}`)}
                  data-testid={`vq-row-${q.quote_no}`}>
                <td className="px-3 py-2 font-mono text-xs text-indigo-700 font-semibold">{q.quote_no}</td>
                <td className="px-3 py-2 text-sm inline-flex items-center gap-1">
                  <Truck className="w-3 h-3 text-gray-400" /> {q.supplier_name}
                </td>
                <td className="px-3 py-2 text-xs">{q.quote_date}</td>
                <td className="px-3 py-2 text-xs">
                  {q.valid_until ? (
                    (() => {
                      const today = new Date().toISOString().slice(0, 10);
                      const expired = q.valid_until < today;
                      return (
                        <span className={expired
                          ? 'px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 font-semibold'
                          : ''}
                              title={expired ? 'Quotation expired' : ''}
                              data-testid={`vq-valid-until-${q.quote_no}`}>
                          {q.valid_until}{expired ? ' · expired' : ''}
                        </span>
                      );
                    })()
                  ) : (
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold inline-flex items-center gap-1"
                          title="No expiry date set on this quotation"
                          data-testid={`vq-valid-until-missing-${q.quote_no}`}>
                      ⚠ missing
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs max-w-[260px]"
                    data-testid={`vq-payment-terms-${q.quote_no}`}>
                  {(() => {
                    // Short labels for the listing — abbreviated to keep the row compact.
                    const ABBR = {
                      advance_payment:             'AP',
                      design_engineering:          'D&E',
                      after_delivery:              'AD',
                      before_delivery:             'BD',
                      after_installation:          'AI',
                      after_testing_commissioning: 'TC',
                      retention:                   'R',
                    };
                    const FULL = {
                      advance_payment:             'Advance Payment',
                      design_engineering:          'Design & Engineering',
                      after_delivery:              'After Delivery',
                      before_delivery:             'Before Delivery',
                      after_installation:          'After Installation',
                      after_testing_commissioning: 'After Testing & Commissioning',
                      retention:                   'Retention',
                    };
                    const bd = q.payment_terms_breakdown || {};
                    const pairs = Object.entries(bd).filter(([, v]) => Number(v) > 0);
                    if (pairs.length > 0) {
                      const tooltip = pairs
                        .map(([k, v]) => `${FULL[k] || k}: ${v}%`).join('\n');
                      return (
                        <div className="flex flex-wrap gap-1" title={tooltip}>
                          {pairs.map(([k, v]) => (
                            <span key={k}
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-violet-50 text-violet-800 border border-violet-200 font-mono text-[10px]">
                              {ABBR[k] || k} <strong>{v}%</strong>
                            </span>
                          ))}
                        </div>
                      );
                    }
                    if (q.payment_terms) {
                      return (
                        <span className="text-gray-700" title={q.payment_terms}>
                          {q.payment_terms.length > 38
                            ? `${q.payment_terms.slice(0, 38)}…`
                            : q.payment_terms}
                        </span>
                      );
                    }
                    return <span className="text-gray-400 italic">—</span>;
                  })()}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">{(q.lines || []).length}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-indigo-700">
                  {(q.grand_total || 0).toLocaleString()} {q.currency}
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${statusTone[q.status] || statusTone.draft}`}>
                    {q.status}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-gray-600">V{q.revision_number}</td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  {q.is_latest && q.status === 'active' && (
                    <button onClick={() => { setBaseQuote(q); setShowForm(true); }}
                            data-testid={`vq-revise-${q.quote_no}`}
                            className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1">
                      Revise <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <QuoteFormModal open={showForm} onClose={() => setShowForm(false)}
                       onSaved={refresh} suppliers={suppliers} items={items}
                       baseQuote={baseQuote} />

      {/* Read-only modal preview of an unpriced Customer RFQ — opens when
          the user clicks a red row in the table. From here they can either
          close (back to list) or deliberately press "Create linked VQ" to
          launch the New Vendor Quotation form pre-linked to this RFQ. */}
      <UnpricedRfqViewer rfq={viewingRfq}
                          onClose={() => setViewingRfq(null)}
                          onCreateLink={(id) => {
                            setViewingRfq(null);
                            navigate(`/vendor-quotations/new?linked_rfq=${id}`);
                          }} />
    </div>
  );
}


// ─────────── Unpriced Customer RFQ — Read-only viewer ────────────
// A lightweight modal that shows the contents of a Customer RFQ when
// the user clicks the red row in the Vendor Quotations table.  The
// pricing team requested a quick peek at the RFQ details first,
// instead of being thrown straight into the New VQ form.
function UnpricedRfqViewer({ rfq, onClose, onCreateLink }) {
  if (!rfq) return null;
  const lines = Array.isArray(rfq.lines) ? rfq.lines : [];
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="unpriced-rfq-viewer"
          onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 bg-red-50 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-red-600 font-semibold">
              Unpriced Customer RFQ · طلب عرض غير مُسعَّر
            </div>
            <div className="text-lg font-bold text-red-800 inline-flex items-center gap-2 mt-0.5">
              <FileText className="w-5 h-5" /> {rfq.rfq_no}
            </div>
          </div>
          <button onClick={onClose} data-testid="unpriced-rfq-viewer-close"
                  className="text-gray-500 hover:text-gray-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Meta grid */}
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-gray-100">
          <div>
            <div className="text-[11px] uppercase text-gray-500">Customer</div>
            <div className="text-sm font-semibold text-gray-900 mt-0.5">{rfq.customer_name || '—'}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-gray-500">Project</div>
            <div className="text-sm font-semibold text-gray-900 mt-0.5">{rfq.project_name || '—'}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-gray-500">Contact</div>
            <div className="text-sm font-semibold text-gray-900 mt-0.5">{rfq.contact_person || '—'}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-gray-500">RFQ Date</div>
            <div className="text-sm font-semibold text-gray-900 mt-0.5">{rfq.rfq_date || '—'}</div>
          </div>
          {rfq.due_date && (
            <div>
              <div className="text-[11px] uppercase text-gray-500">Due Date</div>
              <div className="text-sm font-semibold text-gray-900 mt-0.5">{rfq.due_date}</div>
            </div>
          )}
          {rfq.priority && (
            <div>
              <div className="text-[11px] uppercase text-gray-500">Priority</div>
              <div className="text-sm font-semibold text-gray-900 mt-0.5 capitalize">{rfq.priority}</div>
            </div>
          )}
          {rfq.notes && (
            <div className="col-span-2 md:col-span-4">
              <div className="text-[11px] uppercase text-gray-500">Notes</div>
              <div className="text-sm text-gray-800 mt-0.5 whitespace-pre-wrap">{rfq.notes}</div>
            </div>
          )}
        </div>

        {/* Lines */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-600 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 w-10">#</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-left px-3 py-2">Brand</th>
                <th className="text-left px-3 py-2">Size</th>
                <th className="text-right px-3 py-2">Qty</th>
                <th className="text-left px-3 py-2">UoM</th>
                <th className="text-left px-3 py-2">Type of Work</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">
                  No lines on this RFQ.
                </td></tr>
              )}
              {lines.map((ln, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-xs text-gray-500">{i + 1}</td>
                  <td className="px-3 py-2 text-xs text-gray-900">{ln.description || ln.item_description || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-700">{ln.requested_brand || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-700">{ln.size || '—'}</td>
                  <td className="px-3 py-2 text-xs text-right text-gray-900 font-semibold">{ln.quantity ?? '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-700">{ln.uom || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-700">{ln.type_of_work || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer actions */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {lines.length} line{lines.length === 1 ? '' : 's'} · status: <span className="font-semibold text-red-700">unpriced</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="unpriced-rfq-viewer-cancel"
                    className="px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-100">
              Close
            </button>
            <button onClick={() => onCreateLink(rfq.id)}
                    data-testid="unpriced-rfq-viewer-create-link"
                    className="px-3 py-1.5 rounded text-sm bg-red-600 hover:bg-red-700 text-white font-semibold inline-flex items-center gap-1">
              <Plus className="w-4 h-4" /> Create linked VQ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ───────────────────── New Quotation (Full Page) ─────────────────
export function VendorQuotationNewPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = React.useState([]);
  const [items, setItems] = React.useState([]);
  React.useEffect(() => {
    Promise.all([
      axiosInstance.get('/suppliers?limit=500').catch(() => ({data: {suppliers: []}})),
      axiosInstance.get('/item-master?limit=500').catch(() => ({data: {items: []}})),
    ]).then(([s, i]) => {
      setSuppliers(s.data?.suppliers || (Array.isArray(s.data) ? s.data : []));
      setItems(i.data?.items || []);
    });
  }, []);
  return (
    <div className="p-6 space-y-4" data-testid="vq-new-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold inline-flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-indigo-600" /> New Vendor Quotation
        </h1>
        <button onClick={() => navigate('/vendor-quotations')}
                data-testid="vq-new-page-back"
                className="text-sm text-gray-500 hover:text-gray-800">← Back to list</button>
      </div>
      <QuoteFormModal open={true}
                       onClose={() => navigate('/vendor-quotations')}
                       onSaved={() => navigate('/vendor-quotations')}
                       suppliers={suppliers} items={items}
                       baseQuote={null} inline={true} />
    </div>
  );
}
